import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useDemoData } from "@/hooks/useDemoData";
import {
  Table,
  TableHead,
  TableHeader,
  TableRow,
  TableCell,
  TableBody,
} from "@/components/ui/table";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { useForm } from "react-hook-form";
import { ChevronDown, ChevronRight } from "lucide-react";
import EditVehicleModal from "../modals/EditVehicleModal";

export default function VehiclesTab() {
  const { trucks, addTruck, updateTruck } = useDemoData();

  const [expanded, setExpanded] = useState<string[]>([]);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  
  const activeVehicles = trucks.filter(
    (t) => t.status === "Active" || t.status === "Loading"
  );
  const inactiveVehicles = trucks.filter(
    (t) => t.status != "Active" && t.status != "Loading"
  );

  const toggle = (id: string) =>
    setExpanded((e) =>
      e.includes(id) ? e.filter((x) => x !== id) : [...e, id]
    );

  const handleEditClick = (vehicle: any, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedVehicle(vehicle);
    setEditModalOpen(true);
  };

  const handleSaveEdit = (vehicleId: string, data: any) => {
    updateTruck(vehicleId, data);
  };

  const { register, handleSubmit, reset } = useForm();

  const onSubmit = (data: any) => {
    addTruck({
      carrierId: "current-carrier",
      id: data.id,
      driver: data.driver || "Unassigned",
      model: data.model,
      year: data.year,
      type: data.type,
      maxCapacity: data.maxCapacity,
      maxWeight: data.maxWeight,
      status: "Inactive",
    });
    reset();
  };

  return (
    <div className="gap-6 p-4 space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="flex justify-between items-center">
            <h3 className="text-lg font-semibold mb-4">Active Vehicles</h3>
          </CardHeader>

          <CardContent>
            <Table>
              <TableHeader className="bg-gray-100">
                <TableRow>
                  <TableHead>Model</TableHead>
                  <TableHead>Vehicle ID</TableHead>
                  <TableHead className="text-center">Driver</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {activeVehicles.map((v) => (
                  <React.Fragment key={v.id}>
                    <TableRow
                      className={`hover:bg-gray-200 cursor-pointer ${expanded.includes(v.id) ? "bg-gray-200" : ""}`}
                      onClick={() => toggle(v.id)}
                    >
                      <TableCell>
                        {expanded.includes(v.id) ? (
                          <ChevronDown className="h-4 w-4 inline mr-2" />
                        ) : (
                          <ChevronRight className="h-4 w-4 inline mr-2" />
                        )}
                        {v.model}
                      </TableCell>
                      <TableCell>{v.id}</TableCell>
                      <TableCell className="text-center">{v.driver}</TableCell>
                    </TableRow>
                    {expanded.includes(v.id) && (
                      <TableRow className="">
                        <TableCell colSpan={3} className="p-0 bg-purple-300">
                          <div className="p-4 bg-gray-200 border-t">
                            <div className="grid grid-cols-3 gap-4 mb-3">
                              <p className="text-left">
                                <strong>Vehicle Type</strong>
                              </p>
                              <p className="text-center">
                                <strong>Max Capacity</strong>
                              </p>
                              <p className="text-center">
                                <strong>Status</strong>
                              </p>
                            </div>
                            <div className="grid grid-cols-3 gap-4 mb-3">
                              <p className="text-left">{v.type}</p>
                              <p className="text-center">{v.maxCapacity} lbs</p>
                              <p className="text-center">{v.status}</p>
                            </div>
                            <Button 
                              className="w-full"
                              onClick={(e) => handleEditClick(v, e)}
                            >
                              Edit
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex justify-between items-center">
            <h3 className="text-lg font-semibold mb-4">Inactive Vehicles</h3>
          </CardHeader>

          <CardContent>
            <Table>
              <TableHeader className="bg-gray-100">
                <TableRow>
                  <TableHead>Model</TableHead>
                  <TableHead>Vehicle ID</TableHead>
                  <TableHead className="text-right">Driver</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {inactiveVehicles.map((v) => (
                  <React.Fragment key={v.id}>
                    <TableRow
                      className={`hover:bg-gray-200 cursor-pointer ${expanded.includes(v.id) ? "bg-gray-200" : ""}`}
                      onClick={() => toggle(v.id)}
                    >
                      <TableCell>
                        {expanded.includes(v.id) ? (
                          <ChevronDown className="h-4 w-4 inline mr-2" />
                        ) : (
                          <ChevronRight className="h-4 w-4 inline mr-2" />
                        )}
                        {v.model}
                      </TableCell>
                      <TableCell>{v.id}</TableCell>
                      <TableCell className="text-center">{v.driver}</TableCell>
                    </TableRow>
                    {expanded.includes(v.id) && (
                      <TableRow className="">
                        <TableCell colSpan={3} className="p-0 bg-purple-300">
                          <div className="p-4 bg-gray-200 border-t">
                            <div className="grid grid-cols-3 gap-4 mb-3">
                              <p className="text-left">
                                <strong>Vehicle Type</strong>
                              </p>
                              <p className="text-center">
                                <strong>Max Capacity</strong>
                              </p>
                              <p className="text-center">
                                <strong>Status</strong>
                              </p>
                            </div>
                            <div className="grid grid-cols-3 gap-4 mb-3">
                              <p className="text-left">{v.type}</p>
                              <p className="text-center">{v.maxCapacity} lbs</p>
                              <p className="text-center">{v.status}</p>
                            </div>
                            <Button 
                              className="w-full"
                              onClick={(e) => handleEditClick(v, e)}
                            >
                              Edit
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-white p-4 rounded-lg shadow-sm">
        <h3 className="text-lg font-semibold mb-4">Add / Edit Vehicle</h3>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <Input
            type="text"
            {...register("id", { required: true })}
            placeholder="Vehicle id"
          />
          <Input
            type="text"
            {...register("driver", { required: false })}
            placeholder="Driver"
          />
          <Input
            type="text"
            {...register("model", { required: true })}
            placeholder="Model"
          />
          <Input
            type="number"
            {...register("year", { required: true })}
            placeholder="Vehicle Year"
          />
          <Input
            type="text"
            {...register("type", { required: true })}
            placeholder="Vehicle Type"
          />
          <Input
            type="number"
            {...register("maxCapacity", { required: true })}
            placeholder="Max Capacity"
          />
          <Input
            type="number"
            {...register("maxWeight", { required: true })}
            placeholder="Max Weight"
          />
          <Button type="submit">Save</Button>
        </form>
      </Card>

      {/* Edit Modal */}
      <EditVehicleModal
        vehicle={selectedVehicle}
        isOpen={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        onSave={handleSaveEdit}
      />
    </div>
  );
}